'use client';

import { Checkbox } from '@kit/ui/checkbox';
import { Label } from '@kit/ui/label';

import type { ConsentItem } from '../types';

interface GranularConsentCheckboxesProps {
  /** The consent items to render as individual checkboxes. */
  items: ConsentItem[];

  /** Current checkbox state, keyed by item.id. */
  values: Record<string, boolean>;

  /** Callback when a checkbox value changes. */
  onChange: (id: string, checked: boolean) => void;

  /** Validation errors keyed by item.id. */
  errors?: Record<string, string>;
}

/**
 * Renders a list of granular consent checkboxes.
 *
 * Each ConsentItem maps to a single checkbox. Required items include
 * "(Erforderlich)" or "(Required)" in their text. All checkboxes are
 * unchecked by default. Validation errors display below each checkbox.
 *
 * Uses @kit/ui Checkbox and Label components for consistent styling.
 */
export function GranularConsentCheckboxes({
  items,
  values,
  onChange,
  errors,
}: GranularConsentCheckboxesProps) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => {
        const isChecked = values[item.id] ?? false;
        const error = errors?.[item.id];

        return (
          <div key={item.id} className="flex flex-col gap-1">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) =>
                  onChange(item.id, checked as boolean)
                }
                aria-invalid={!!error}
                data-test={`consent-checkbox-${item.id}`}
              />

              <Label className="text-muted-foreground cursor-pointer text-xs leading-relaxed font-normal">
                {item.text}
              </Label>
            </div>

            {error ? (
              <p
                className="text-destructive pl-7 text-[0.8rem] font-medium"
                role="alert"
              >
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
